import { notifications } from "@mantine/notifications";

import { handleApiSuccess, showAlert } from "@/utils/responseHandler";

import type {
  AssignmentCategoryNode,
  AssignmentsTreeResponse,
} from "@/api/form-builder-assignments/formBuilderAssignments.types";

export type PublishedFormAssignment = "assigned" | "fallbackOnly" | "unassigned";

function categoriesHold(categories: AssignmentCategoryNode[], templateId: string): boolean {
  return categories.some(
    (category) =>
      category.assignments.some((assignment) => assignment.templateId === templateId) ||
      (category.inheritedDefaults ?? []).some((inherited) => inherited.templateId === templateId),
  );
}

export function getPublishedFormAssignment(
  tree: AssignmentsTreeResponse,
  templateId: string,
): PublishedFormAssignment {
  const isAssigned = tree.entities.some(
    (entity) =>
      categoriesHold(entity.categories, templateId) ||
      entity.clients.some(
        (client) =>
          categoriesHold(client.categories, templateId) ||
          client.brands.some((brand) => categoriesHold(brand.categories, templateId)),
      ),
  );
  if (isAssigned) return "assigned";

  const isFallback = tree.entities.some(
    (entity) => entity.fallbackDefault?.templateId === templateId,
  );
  return isFallback ? "fallbackOnly" : "unassigned";
}

export const ASSIGNMENT_CHECK_TIMEOUT_MS = 4000;

// Publishing already succeeded, so a failed or slow check must never turn the
// confirmation into a warning: anything but a classified tree reads as assigned.
export async function resolvePublishedFormAssignment(
  templateId: string | undefined,
  fetchTree: (signal: AbortSignal) => Promise<AssignmentsTreeResponse>,
): Promise<PublishedFormAssignment> {
  if (!templateId) return "assigned";
  try {
    const tree = await fetchTree(AbortSignal.timeout(ASSIGNMENT_CHECK_TIMEOUT_MS));
    return getPublishedFormAssignment(tree, templateId);
  } catch {
    return "assigned";
  }
}

const NEXT_STEP_MESSAGES: Record<Exclude<PublishedFormAssignment, "assigned">, string> = {
  unassigned: "It is not assigned yet, so technicians won't receive it until you assign it.",
  fallbackOnly: "It is only used as the fallback form; assign it to a slot to use it as a default.",
};

interface NotifyFormPublishedOptions {
  formId: string | undefined;
  formName: string;
  assignment: PublishedFormAssignment;
  onGoToAssignments: () => void;
}

export function notifyFormPublished({
  formId,
  formName,
  assignment,
  onGoToAssignments,
}: NotifyFormPublishedOptions): void {
  if (assignment === "assigned") {
    handleApiSuccess(`Form "${formName}" has been published successfully.`, "Published");
    return;
  }

  const toastId = `form-published-${formId ?? formName}`;
  showAlert({
    id: toastId,
    title: "Published",
    message: `Form "${formName}" has been published. ${NEXT_STEP_MESSAGES[assignment]}`,
    type: "info",
    autoClose: false,
    actions: [
      {
        label: "Go to Assignments",
        onClick: () => {
          notifications.hide(toastId);
          onGoToAssignments();
        },
      },
    ],
  });
}
